import { Alert, Tabs } from 'antd';
import { FileX, ShieldAlert } from 'lucide-react';
import type { ReactElement } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router';
import { useCurrentUser } from '../../../shared/features/session/components/CurrentUserProvider';
import { DetailPageSkeleton } from '../../../shared/components/DetailPageSkeleton';
import { EmptyState } from '../../../shared/components/EmptyState';
import { PageHeader } from '../../../shared/components/PageHeader';
import { withinSection } from '../../../shared/components/section-trail';
import { buildJobDetailTabs } from '../components/build-job-detail-tabs';
import { JOB_TABS, jobTabPath, type JobTab } from '../jobs.paths';
import { JobCreditHoldPanel } from '../features/credit-hold/components/JobCreditHoldPanel';
import { JobManualPricePanel } from '../features/manual-pricing/components/JobManualPricePanel';
import { useJobManualPrice } from '../features/manual-pricing/hooks/use-job-manual-price';
import { JobQuotePanel } from '../features/quote/components/JobQuotePanel';
import { JobStateActions } from '../components/JobStateActions';
import { useJobActions } from '../hooks/use-job-actions';
import { useJobCatalogues } from '../hooks/use-job-catalogues';
import { useJobCreditHold } from '../features/credit-hold/hooks/use-job-credit-hold';
import { useJobQuote } from '../features/quote/hooks/use-job-quote';
import { useJobRecord, type JobRecordState } from '../hooks/use-job-record';

const JOB_TRAIL = withinSection('jobs');

/** Stands in for the job until it has loaded, so the manual price hook can be called unconditionally. */
const NO_JOB = { id: '', number: '', pricingRequired: false };

function jobOrPlaceholder(state: JobRecordState) {
  return state.status === 'ready' && state.job ? state.job : NO_JOB;
}

function jobLane(originZoneName: string | null, destinationZoneName: string | null): string {
  if (!originZoneName && !destinationZoneName) return '';
  return `${originZoneName ?? '—'} → ${destinationZoneName ?? '—'}`;
}

interface JobActionAlertsProps {
  confirmBlockedError: string | null;
  quoteBlockedError: string | null;
  conflictError: string | null;
  creditHoldConflictError: string | null;
  quoteConflictError: string | null;
}

/** The page's inline action alerts, pulled out so `JobDetailPage` itself stays under the complexity limit. */
function JobActionAlerts({ confirmBlockedError, quoteBlockedError, conflictError, creditHoldConflictError, quoteConflictError }: JobActionAlertsProps) {
  return (
    <>
      {confirmBlockedError && <Alert className="mb-5" type="error" title={confirmBlockedError} showIcon />}
      {quoteBlockedError && <Alert className="mb-5" type="error" title={quoteBlockedError} showIcon />}
      {conflictError && <Alert className="mb-5" type="warning" title={conflictError} showIcon />}
      {creditHoldConflictError && <Alert className="mb-5" type="warning" title={creditHoldConflictError} showIcon />}
      {quoteConflictError && <Alert className="mb-5" type="warning" title={quoteConflictError} showIcon />}
    </>
  );
}

/** The page's four non-ready states, pulled out so `JobDetailPage` itself stays under the complexity limit. Returns `undefined` once there's a job to show. */
function jobLoadStateFallback(state: JobRecordState): ReactElement | undefined {
  if (state.status === 'forbidden') return <Navigate to="/" replace />;
  if (state.status === 'loading') return <DetailPageSkeleton label="Loading job" breadcrumbs={JOB_TRAIL} tabs={JOB_TABS.length} />;
  if (state.status === 'not_found') {
    return <EmptyState icon={FileX} title="Job not found" description="This job doesn't exist, or the link is out of date." />;
  }
  if (state.status === 'error' || !state.job) {
    return <EmptyState icon={ShieldAlert} title="Couldn't load this job" description="Something went wrong loading this job. Try reloading the page." />;
  }
  return undefined;
}

/**
 * A job's own page (gitaumoses4/chukua#39): header with its state and lane, state actions
 * available per permission and per status, and its cargo/terms/attachments/readiness/trips/
 * history on their own tabs. `job.view` alone sees everything read-only — state actions and
 * the Details tab's save button need `job.create` (CLAUDE.md: nav-hiding is UX only).
 */
export function JobDetailPage({ tab }: { tab: JobTab }) {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const { state, saveError, saving, update, reload } = useJobRecord(id);
  const actions = useJobActions(id, reload);
  const creditHold = useJobCreditHold(reload);
  const manualPrice = useJobManualPrice(jobOrPlaceholder(state), reload);
  const quote = useJobQuote(reload);
  const catalogues = useJobCatalogues();
  const { state: currentUser } = useCurrentUser();
  const canManage = currentUser.status === 'authenticated' && currentUser.user.permissions.includes('job.create');

  const fallback = jobLoadStateFallback(state);
  if (fallback) return fallback;
  if (state.status !== 'ready' || !state.job) return null;

  const { job } = state;
  const { jobTypes, containerSizes, containerTypes, specialHandlingRequirements } =
    catalogues.status === 'loaded' ? catalogues : { jobTypes: [], containerSizes: [], containerTypes: [], specialHandlingRequirements: [] };

  return (
    <>
      <PageHeader
        breadcrumbs={JOB_TRAIL}
        title={job.number}
        description={[job.customerName, jobLane(job.originZoneName, job.destinationZoneName)].filter(Boolean).join(' · ')}
        actions={<JobStateActions job={job} canManage={canManage} actions={actions} />}
      />
      <JobQuotePanel job={job} quote={quote} />
      <JobCreditHoldPanel job={job} creditHold={creditHold} />
      <JobManualPricePanel job={job} manualPrice={manualPrice} />
      <JobActionAlerts
        confirmBlockedError={actions.confirmBlockedError}
        quoteBlockedError={actions.quoteBlockedError}
        conflictError={actions.conflictError}
        creditHoldConflictError={creditHold.conflictError}
        quoteConflictError={quote.conflictError}
      />
      <Tabs
        activeKey={tab}
        onChange={(key) => navigate(jobTabPath(job.id, key as JobTab))}
        items={buildJobDetailTabs({
          job,
          catalogues,
          jobTypes,
          containerSizes,
          containerTypes,
          specialHandlingRequirements,
          updateError: saveError,
          saving,
          canManage,
          onUpdate: update,
          onAttachmentUploaded: reload,
          onChanged: reload,
        })}
      />
    </>
  );
}
